/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ErrorPriority, MetadataState, PlaybackContentType } from "../enums";
import {
  ConnectivityErrorCode,
  GenericErrorCode,
  PlaybackSetupErrorCode,
  RestrictionErrorCode,
  SessionErrorCode
} from "./PlaybackErrorCodes";
import { playerErrorCodes, PLAYER_ERROR_SEPARATOR } from "./PlayerErrorCodes";

/**
 * The two display surfaces the error framework arbitrates between.
 *
 * - `OSD` — the non-blocking on-screen message rendered by `OsdContainer`.
 * - `DIALOG` — the full-screen modal rendered by `UnityDialogContainer`.
 *
 * These are the only two surfaces under the framework's control. `AlertOverlay`
 * (sign-in / app-startup) is deliberately outside it.
 */
export const ErrorSurface = {
  OSD: "OSD",
  DIALOG: "DIALOG"
} as const;

export type ErrorSurface = (typeof ErrorSurface)[keyof typeof ErrorSurface];

/**
 * Result of routing an error code to a surface.
 */
export interface ErrorRoute {
  /** The surface the error should be shown on. */
  surface: ErrorSurface;
  /** The priority the error competes at on that surface. */
  priority: ErrorPriority;
}

/**
 * Codes that surface as a full-screen dialog.
 *
 * A `Set`, not a `Map`: every dialog-routed code intentionally competes at the
 * same priority, {@link ErrorPriority.SESSION_AND_PLAYER_ERROR}. Kaveri does
 * the same — `PlaybackErrorDisplay.showOverlay` hardcodes that one priority for
 * its whole error-overlay set rather than assigning one per code. A consequence
 * worth knowing: because all dialog codes tie, the `>=` guard in
 * `unityDialogSlice` means the *first* dialog of a burst is the one the user
 * sees, not the last.
 *
 * **If overlay-specific priority is ever needed**, model it exactly like
 * {@link OSD_ERROR_PRIORITIES} rather than inventing a second mechanism:
 *   1. promote this to `ReadonlyMap<string, ErrorPriority>`, keeping the
 *      grouping comments;
 *   2. in {@link resolveErrorSurface}, return the mapped value instead of the
 *      `SESSION_AND_PLAYER_ERROR` constant;
 *   3. switch {@link ROUTED_ERROR_CODES}.dialog to `Array.from(...keys())`.
 * Nothing outside this file changes: `ErrorRoute.priority` is already per-call,
 * the arbitration guards already compare numerically, and the drift-guard test
 * compares code lists only.
 *
 * Built once at module load; kaveri rebuilds the equivalent Set on every
 * routing call.
 */
const DIALOG_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  // Shaka player categories (compound codes strip to these bases)
  playerErrorCodes.PLAYER_BAD_INPUT,
  playerErrorCodes.PLAYER_PARSER_FAILURE,
  playerErrorCodes.PLAYER_SOURCE_EXCEPTION,
  playerErrorCodes.PLAYER_ILLEGAL_STATE,
  playerErrorCodes.DECODER_QUERY_FAILED,
  playerErrorCodes.DECODER_INITIALIZATION_FAILED,
  playerErrorCodes.DECODER_CRYPTO_FAILED,
  playerErrorCodes.SUBTITLE_DECODER_FAILED,
  playerErrorCodes.PLAYER_RENDER_FAILED,
  playerErrorCodes.PLAYER_DRM_OUTPUT_PROTECTION_ERROR,
  playerErrorCodes.PLAYER_DRM_SECURITY_LEVEL_ERROR,
  playerErrorCodes.DRM_DECRYPTION_KEY_MISSING,
  playerErrorCodes.MANIFEST_SOCKET_OPEN_ERROR,
  playerErrorCodes.MANIFEST_INVALID_HTTP_RESPONSE,
  playerErrorCodes.SEGMENT_SOCKET_OPEN_ERROR,
  playerErrorCodes.SEGMENT_INVALID_HTTP_RESPONSE,
  playerErrorCodes.SOCKET_OPEN_EXCEPTION,
  playerErrorCodes.INVALID_HTTP_RESPONSE,
  playerErrorCodes.DRM_SYSTEM_ERROR,
  playerErrorCodes.DRM_NOT_SUPPORTED_ERROR,
  playerErrorCodes.DRM_CONTENT_ERROR,
  playerErrorCodes.DRM_LICENSE_EXPIRED_ERROR,
  playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR,
  playerErrorCodes.DRM_UNSPECIFIED_ERROR,
  // Session-create / keep-alive failures
  SessionErrorCode.GENERIC_LIVE_ERROR,
  SessionErrorCode.GENERIC_VOD_ERROR,
  SessionErrorCode.GENERIC_SOCU_ERROR,
  SessionErrorCode.GENERIC_NDVR_ERROR,
  SessionErrorCode.INTERNAL_ERROR,
  SessionErrorCode.NOT_AUTHENTICATED,
  SessionErrorCode.NOT_REGISTERED,
  SessionErrorCode.TOO_MANY_STREAMS,
  SessionErrorCode.TOO_MANY_STREAMS_FOR_RESOURCE,
  SessionErrorCode.AUTHENTICATION_EXPIRED,
  SessionErrorCode.NOT_AUTHORIZED,
  SessionErrorCode.NO_SESSION,
  SessionErrorCode.UNKNOWN_ERROR,
  SessionErrorCode.NDVR_NOT_AUTHORIZED,
  // Playback-setup failures
  PlaybackSetupErrorCode.NOT_READY_FOR_PLAYBACK,
  PlaybackSetupErrorCode.AIRING_DATA_FAILED,
  PlaybackSetupErrorCode.DEVICE_MISSING_CONFIG,
  PlaybackSetupErrorCode.PLAYLIST_URI_MISSING,
  PlaybackSetupErrorCode.VP_CONFIGURATIONS_MISCONFIGURATION,
  PlaybackSetupErrorCode.VP_CONFIGURATIONS_EMPTY_RESPONSE,
  PlaybackSetupErrorCode.UNSUPPORTED_CONTENT,
  PlaybackSetupErrorCode.GRACE_PERIOD_ENDED,
  // Cross-cutting generic fallback
  GenericErrorCode.UNKNOWN_ERROR
]);

/**
 * Codes that surface as an OSD, each with its own priority.
 *
 * Note the deliberate split between the two location priorities:
 * {@link ErrorPriority.LOCATION_RESTRICTION} (996) is reserved for `V418` —
 * the in-home *location service* being unreachable — while out-of-home and
 * out-of-region are ordinary business-rule restrictions at
 * {@link ErrorPriority.GENERAL_RESTRICTIONS} (995). This mirrors kaveri.
 */
const OSD_ERROR_PRIORITIES: ReadonlyMap<string, ErrorPriority> = new Map<
  string,
  ErrorPriority
>([
  [ConnectivityErrorCode.NO_NETWORK, ErrorPriority.NO_NETWORK_ERROR],
  [
    RestrictionErrorCode.LOCATION_RESTRICTION,
    ErrorPriority.LOCATION_RESTRICTION
  ],
  [RestrictionErrorCode.BLACKLISTED, ErrorPriority.GENERAL_RESTRICTIONS],
  [RestrictionErrorCode.OUT_OF_HOME, ErrorPriority.GENERAL_RESTRICTIONS],
  [RestrictionErrorCode.OUT_OF_REGION, ErrorPriority.GENERAL_RESTRICTIONS],
  [RestrictionErrorCode.DEVICE_RESTRICTED, ErrorPriority.GENERAL_RESTRICTIONS],
  [RestrictionErrorCode.UNSUBSCRIBED, ErrorPriority.GENERAL_RESTRICTIONS]
]);

/**
 * Every code the routing table knows about, by surface.
 *
 * Exported so tests can assert that each catalogued code is actually covered by
 * a case, rather than silently drifting when a code is added to a table above.
 * Not intended for production use — call {@link resolveErrorSurface} instead.
 */
export const ROUTED_ERROR_CODES: {
  readonly dialog: readonly string[];
  readonly osd: readonly string[];
} = {
  dialog: Array.from(DIALOG_ERROR_CODES),
  osd: Array.from(OSD_ERROR_PRIORITIES.keys())
};

/**
 * Strip the sub-error suffix from a compound error code.
 *
 * Player errors carry a `Vxxx-NNN` code where `NNN` is a Shaka sub-code or a
 * DRM HTTP status (see {@link playerErrorCodes}). Routing is decided by the
 * base code alone, so `V956-006` routes exactly as `V956` and `V948-401`
 * exactly as `V948`.
 *
 * @param code - A bare or compound error code.
 * @returns The base code — the portion before the first separator.
 */
export function getBaseErrorCode(code: string): string {
  return code.split(PLAYER_ERROR_SEPARATOR)[0];
}

/**
 * Decide which surface an error code belongs on, and at what priority.
 *
 * Lookup order — dialog set, then OSD map, then the default. An unrecognised
 * code is NOT an error: it falls back to an OSD at
 * {@link ErrorPriority.GENERAL_RESTRICTIONS}, matching kaveri, so a producer
 * can raise a code before it is catalogued and still get a sane surface.
 *
 * Pure — no Redux, no React, no `shaka-player` dependency — so it is directly
 * unit-testable and safe to call from any layer.
 *
 * @param code - The error code to route. An empty string is valid (an error
 *   with no user-visible code, e.g. a parental-control lock) and routes to the
 *   OSD default.
 * @returns The surface and priority for this code.
 */
export function resolveErrorSurface(code: string): ErrorRoute {
  const baseCode: string = getBaseErrorCode(code);

  if (DIALOG_ERROR_CODES.has(baseCode)) {
    return {
      surface: ErrorSurface.DIALOG,
      // One shared priority for every dialog code, by design — see
      // DIALOG_ERROR_CODES for why, and for how to make it per-code if that is
      // ever needed.
      priority: ErrorPriority.SESSION_AND_PLAYER_ERROR
    };
  }

  const osdPriority: ErrorPriority | undefined =
    OSD_ERROR_PRIORITIES.get(baseCode);
  if (osdPriority !== undefined) {
    return { surface: ErrorSurface.OSD, priority: osdPriority };
  }

  return {
    surface: ErrorSurface.OSD,
    priority: ErrorPriority.GENERAL_RESTRICTIONS
  };
}

/**
 * Metadata-fetch failures that leave playback unable to start, keyed by the
 * content type they apply to.
 *
 * `ACTION_LIST_ERROR` is emitted for LIVE, SOCU and NDVR alike, but only
 * blocks SOCU/NDVR — LIVE's action list only feeds Start Over/Record
 * affordances and playback already started without it. That is why this is a
 * `(MetadataState, PlaybackContentType)` table rather than a flat `Set`.
 *
 * VOD blocks only on `ACTION_DETAILS_ERROR`: without the watch action there is
 * no provider to configure DRM for and nothing to create a session against.
 * `VOD_ACTION_ERROR` (onepass/bookmark actions) and `VOD_PREVIEW_ERROR` only
 * cost the UI its affordances and info, so they are intentionally absent here.
 * `RECORDING_ACTION_INIT_READY` is declared on {@link MetadataState} but never
 * emitted by `MetadataManager`.
 */
const BLOCKING_METADATA_ERRORS: ReadonlyMap<
  PlaybackContentType,
  ReadonlySet<MetadataState>
> = new Map<PlaybackContentType, ReadonlySet<MetadataState>>([
  [
    PlaybackContentType.NDVR_LIVE,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.RECORDING_ACTION_ERROR
    ])
  ],
  [
    PlaybackContentType.NDVR_RECORDED,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.RECORDING_ACTION_ERROR
    ])
  ],
  [
    PlaybackContentType.STARTOVER_BEGINNING,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.ACTION_DETAILS_ERROR
    ])
  ],
  [
    PlaybackContentType.STARTOVER_RESUME,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.ACTION_DETAILS_ERROR
    ])
  ],
  [
    PlaybackContentType.CATCHUP_BEGINNING,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.ACTION_DETAILS_ERROR
    ])
  ],
  [
    PlaybackContentType.CATCHUP_RESUME,
    new Set<MetadataState>([
      MetadataState.ACTION_LIST_ERROR,
      MetadataState.ACTION_DETAILS_ERROR
    ])
  ],
  [
    PlaybackContentType.VOD,
    new Set<MetadataState>([MetadataState.ACTION_DETAILS_ERROR])
  ]
]);

/**
 * Every (content type, state) pair the blocking table knows about.
 *
 * Exported so tests can assert the table's contents directly rather than
 * only through {@link isBlockingMetadataError}'s boolean result.
 */
export const BLOCKING_METADATA_ERROR_STATES: ReadonlyMap<
  PlaybackContentType,
  readonly MetadataState[]
> = new Map(
  Array.from(BLOCKING_METADATA_ERRORS.entries()).map(
    ([contentType, states]) => [contentType, Array.from(states)]
  )
);

/**
 * Whether a metadata `*_ERROR` state means playback cannot start for the
 * given content type — as opposed to a non-blocking metadata failure (e.g.
 * LIVE preview or NDVR last-viewed-position) that playback continues through.
 *
 * Unlisted (state, contentType) pairs are non-blocking by default: a metadata
 * failure only blocks when it is known to gate player setup.
 *
 * @param state - The `MetadataState` reported by `MetadataManager`.
 * @param contentType - The content type of the current playable.
 * @returns Whether the error should be surfaced as a blocking dialog.
 */
export function isBlockingMetadataError(
  state: MetadataState,
  contentType: PlaybackContentType
): boolean {
  const blockingStates: ReadonlySet<MetadataState> | undefined =
    BLOCKING_METADATA_ERRORS.get(contentType);
  return blockingStates !== undefined && blockingStates.has(state);
}
